/**
 * Вставка и диктовка во ВСЕ поля сайта (владелец 30.10.2026: «иногда не вставляется то, что я
 * диктую и пытаюсь через Ctrl+V поставить» — сначала в переписке с партнёром, потом в окне
 * «Отклонить обращение»; диктует через Wispr Flow; «сделай вики тоже»). Ставится один раз в `main.tsx`. Причины — не в конкретном поле:
 *
 * 1. Ctrl+V на Mac браузер не вставляет (там ⌘V). Ловим Ctrl+V в поле ввода, читаем буфер через
 *    Clipboard API и повторяем обычную вставку: сначала событие paste с содержимым буфера — его
 *    ловят свои обработчики (скриншот в переписку, вставка таблиц); не отменили — вставляем текст.
 * 2. Программы диктовки и расширения ставят текст через `el.value = …` и шлют своё событие input.
 *    React такую правку не видит: его «последнее значение» поля (`_valueTracker`) обновилось вместе
 *    с value — состояние пустое, кнопка «Отправить / Отклонить» серая, а при следующей перерисовке
 *    текст стирается. На недоверенных (не от клавиатуры человека) input/change сбрасываем это
 *    значение — React замечает правку и вызывает onChange как при обычном наборе.
 * 3. Chrome на Mac (155; ошибка Chromium 568453825, исправление fc5529f ждут в 157) иногда теряет
 *    вставку: событие paste с текстом приходит, а в поле ничего не попадает и input не случается —
 *    буфер успел смениться посреди вставки. Так и бывает с Wispr Flow: он кладёт продиктованное в
 *    буфер, жмёт ⌘V и возвращает прежний буфер (тот же сбой с Wispr у open-mercato/cezar#1701).
 *    Обычную вставку не трогаем: если после настоящего paste поле не изменилось — вставляем текст
 *    из события сами (`guardPasteInsertion`).
 *
 * Копия CRM: `frontend/src/lib/paste-fixes.ts` (разные пакеты) — держать одинаковыми.
 * Чистые части покрыты `paste-fixes.test.ts`.
 */
import { readClipboardForPaste } from '../components/opening-tasks/clipboard-files';
import { isMacCtrlPaste, isMacPlatform } from '../components/opening-tasks/composer-keys';

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number']);

interface FieldLike {
  tagName?: string;
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isContentEditable?: boolean;
}

/** Текстовое поле input/textarea, в которое сейчас можно писать. */
export function isTextField(el: unknown): boolean {
  if (!el || typeof el !== 'object') return false;
  const f = el as FieldLike;
  const tag = (f.tagName ?? '').toUpperCase();
  if (f.disabled || f.readOnly) return false;
  if (tag === 'TEXTAREA') return true;
  return tag === 'INPUT' && TEXT_INPUT_TYPES.has((f.type || 'text').toLowerCase());
}

/** Куда человек вставляет текст: текстовое поле или редактор (contenteditable). */
export function isTextEntry(el: unknown): boolean {
  return isTextField(el) || Boolean(el && typeof el === 'object' && (el as FieldLike).isContentEditable);
}

/**
 * Сбросить «последнее значение», которое React помнит у поля (`_valueTracker`, внутреннее —
 * так же устроено с React 16 по 19). Любое значение, отличное от текущего: React сравнит его с
 * тем, что в поле, и вызовет onChange. Нет счётчика (не React-поле) — ничего не делаем.
 */
export function nudgeReactValueTracker(el: unknown): boolean {
  const node = el as { value?: unknown; _valueTracker?: { setValue?: (v: string) => void } } | null;
  const tracker = node?._valueTracker;
  if (!tracker || typeof tracker.setValue !== 'function') return false;
  tracker.setValue(`${String(node?.value ?? '')}\u0000`);
  return true;
}

/** Текст в место курсора с историей отмены; React узнаёт о нём через обычный input. */
function insertTextInto(target: HTMLElement, text: string): void {
  target.focus();
  // execCommand устарел, но только он вставляет с историей отмены (Ctrl+Z / ⌘Z); есть во всех браузерах.
  if (document.execCommand('insertText', false, text)) return;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
    const end = target.value.length;
    target.setRangeText(text, target.selectionStart ?? end, target.selectionEnd ?? end, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

/** Минимум от события paste (в проверке — обычный объект). */
export interface PasteEventLike {
  isTrusted: boolean;
  defaultPrevented: boolean;
  target: EventTarget | null;
  clipboardData: Pick<DataTransfer, 'getData'> | null;
}

/**
 * Страховка от потерянной вставки (п. 3). Зовётся на document в фазе всплытия — после обработчиков
 * полей: кто отменил вставку (скриншот в переписку) — тех не трогаем. Только настоящая вставка
 * (`isTrusted`; наша синтетическая для Ctrl+V на Mac вставляет текст сама) в текстовое поле.
 * Ждём до следующей задачи: пришёл input или поле изменилось — браузер вставил сам, ничего не делаем.
 */
export function guardPasteInsertion(e: PasteEventLike, schedule: (fn: () => void) => void = (fn) => void setTimeout(fn, 0)): void {
  if (!e.isTrusted || e.defaultPrevented || !isTextField(e.target)) return;
  let text = '';
  try {
    text = e.clipboardData?.getData('text/plain') ?? '';
  } catch {
    return;
  }
  if (!text) return;
  const field = e.target as HTMLTextAreaElement | HTMLInputElement;
  const value = field.value;
  let start: number | null = null;
  let end: number | null = null;
  try {
    start = field.selectionStart;
    end = field.selectionEnd;
  } catch {
    // email/number: выделения нет — вставим в курсор как есть.
  }
  let inserted = false;
  const onInput = () => {
    inserted = true;
  };
  field.addEventListener('input', onInput, true);
  schedule(() => {
    field.removeEventListener('input', onInput, true);
    if (inserted || field.value !== value || document.activeElement !== field) return;
    try {
      if (start != null && end != null) field.setSelectionRange(start, end);
    } catch {
      // поле без выделения — вставим в текущий курсор
    }
    field.focus();
    // Только execCommand: он, как обычная вставка, соблюдает maxLength поля и историю отмены.
    document.execCommand('insertText', false, text.replace(/\r\n?/g, '\n'));
  });
}

/** Вставить текст как при обычной вставке: событие paste для своих обработчиков, затем сам текст. */
async function pasteFromClipboard(target: HTMLElement, onError: (message: string) => void): Promise<void> {
  let got: { text: string; files: File[] };
  try {
    got = await readClipboardForPaste(navigator.clipboard);
  } catch {
    onError('Браузер не дал прочитать буфер обмена — вставьте через ⌘V');
    return;
  }
  const text = got.text.replace(/\r\n?/g, '\n');
  if (!text && !got.files.length) return;
  let handled = false;
  try {
    const data = new DataTransfer();
    if (text) data.setData('text/plain', text);
    for (const f of got.files) data.items.add(f);
    handled = !target.dispatchEvent(new ClipboardEvent('paste', { clipboardData: data, bubbles: true, cancelable: true }));
  } catch {
    // Браузер без new DataTransfer() — просто вставим текст ниже.
  }
  if (handled || !text) return;
  insertTextInto(target, text);
}

export function installPasteFixes({
  onError,
  isMac = typeof navigator !== 'undefined' && isMacPlatform(navigator),
}: {
  onError: (message: string) => void;
  isMac?: boolean;
}): void {
  if (typeof document === 'undefined') return;
  // Фаза перехвата на document — раньше, чем React (его обработчики на #root) сверяет значение.
  const onUntrusted = (e: Event) => {
    if (!e.isTrusted && isTextField(e.target)) nudgeReactValueTracker(e.target);
  };
  document.addEventListener('input', onUntrusted, true);
  document.addEventListener('change', onUntrusted, true);
  if (!isMac) return;
  // Потерянная вставка Chrome — ошибка только macOS. Всплытие (не перехват): после обработчиков
  // React на #root — видим, отменил ли кто-то вставку.
  document.addEventListener('paste', (e) => guardPasteInsertion(e));
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.defaultPrevented || !isMacCtrlPaste(e, true) || !isTextEntry(e.target)) return;
      e.preventDefault();
      void pasteFromClipboard(e.target as HTMLElement, onError);
    },
    true,
  );
}
